/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: types.ts (Logging Settings Types)
 🗓️ Created: 2025-01-20
 🧩 Description:
    TypeScript types and interfaces for FreeSWITCH logging settings.

 🔁 Last Updated: 2025-01-20 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

// Log levels for FreeSWITCH
export type LogLevel = 'debug' | 'info' | 'notice' | 'warning' | 'err' | 'crit' | 'alert';

// Console Logging Configuration
export interface ConsoleMapping {
  name: string; // Target name (filename, function, or "all")
  levels: LogLevel[]; // Enabled log levels
}

export interface ConsoleSettings {
  colorize: boolean; // Enable ANSI color highlighting
  loglevel: LogLevel[]; // Enabled log levels (multiple selection)
  mappings: ConsoleMapping[]; // Console mappings
}

// File Logging Configuration
export interface FileProfileMapping {
  name: string; // Target name (filename, function, or "all")
  levels: LogLevel[]; // Enabled log levels
}

export interface FileProfile {
  name: string; // Profile name
  logfile: string; // Log file path
  rollover: number; // Max file size in bytes (0 = infinite)
  maximum_rotate: number; // Max rotated files to keep
  uuid_prefix: boolean; // Prefix with session UUID
  mappings: FileProfileMapping[]; // Profile mappings
}

export interface FileLogging {
  rotate_on_hup: boolean; // Rotate on HUP signal
  profiles: FileProfile[]; // File logging profiles
}

// Main Logging Settings
export interface LoggingSettings {
  id?: string;
  uuid?: string;
  domainId: number;
  consoleSettings: ConsoleSettings;
  fileLogging: FileLogging;
  consoleXml?: string; // Generated XML for console.conf.xml
  fileXml?: string; // Generated XML for logfile.conf.xml
  enabled: boolean;
  createdAt?: string;
  updatedAt?: string;
}

// Default values
export const DEFAULT_CONSOLE_SETTINGS: ConsoleSettings = {
  colorize: true,
  loglevel: ['notice', 'warning', 'err', 'crit', 'alert'], // Default: notice and above
  mappings: [
    {
      name: 'all',
      levels: ['debug', 'info', 'notice', 'warning', 'err', 'crit', 'alert']
    }
  ]
};

export const DEFAULT_FILE_LOGGING: FileLogging = {
  rotate_on_hup: true,
  profiles: [
    {
      name: 'default',
      logfile: '/var/log/freeswitch.log',
      rollover: 1048576000, // 1GB
      maximum_rotate: 32,
      uuid_prefix: true,
      mappings: [
        {
          name: 'all',
          levels: ['debug', 'info', 'notice', 'warning', 'err', 'crit', 'alert']
        }
      ]
    }
  ]
};

export const DEFAULT_LOGGING_SETTINGS: LoggingSettings = {
  domainId: 0,
  consoleSettings: DEFAULT_CONSOLE_SETTINGS,
  fileLogging: DEFAULT_FILE_LOGGING,
  enabled: true
};

// Log level options for UI
export const LOG_LEVEL_OPTIONS: LogLevel[] = [
  'debug',
  'info',
  'notice',
  'warning',
  'err',
  'crit',
  'alert'
];

